import React from "react";
import EntryGridAr from "../../../components/EntryGridAr";
import SectionCard from "../components/SectionCard";
import { renderSampleSectionBody } from "./sampleSectionRenderer";
import { renderSectionBodyContent } from "./sectionBodyRenderer";

// セクション描画レイアウト一式をまとめて返す。
export function createSectionLayoutRenderer(deps) {
    const {
        PANEL_TYPE,
        fnc,
        formid,
        isEmpty,
        isEnabled,
        DicVal,
        columns,
        columninfo,
        entrydataRef,
        EntrybodyRef,
        ensureTotalDataRef,
        ensureTotalColRef,
        onFirstTotalDataRendered,
        storeTotalRowData,
        buildTotalGridColumns,
        buildTotalPanel,
        totalTitle,
        totalContainerStyle,
        normalizeGenkaSummaryRows,
        genkaSummaryFixedHeight,
        resolveSectionPanel,
        resolveRemarksSectionPanel,
        resolveGridSectionPanel,
        sectionTotalType,
        sectionGridRowStyle,
        sectionRenderModelResolver,
        resolveGridIndex,
        EmptyPanel,
        Contents,
        renderActionArea,
        renderSustainableImagePanel,
        renderGenkaHeader,
        renderDemandHeader,
        buildDemandColumns,
        buildNouhinHeaderColumns,
        buildGenkaFixedColumns,
        buildBrandNameGridColumns,
        buildDemeritGridColumns,
        colorwayDetailParams,
        toStableAutoHeightPanel,
        genkaGridFixedHeight,
        isDemandSectionId,
        collapsedSections,
        sectionclose,
        sectionRefs,
        setCollapsedSections,
        renderSectionHeaderAction
    } = deps;

    // 合計グリッドの外枠を描画する。
    function renderTotalGridContainer(section, sideSummary, title, totalGrid) {
        return (
            <div className={section.id === "genka" ? "kakou-genka-total-grid" : ""} style={totalContainerStyle(section, sideSummary)}>
                {!isEmpty(title) ? (
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>
                        {title}
                    </div>
                ) : null}
                {totalGrid}
            </div>
        );
    }

    // 合計グリッドを描画する。
    function renderTotalGrid(section, gridPanel, totaltype, sideSummary, compact) {
        if (isEmpty(gridPanel) || isEmpty(totaltype)) return null;
        const { totalPanel, resolvedTotalType } = buildTotalPanel(section, gridPanel, totaltype);
        const totalDataRef = ensureTotalDataRef(totalPanel);

        // 原価合計は固定表示順へ正規化してから表示する。
        if (section.id === "genka") {
            totalDataRef.current = normalizeGenkaSummaryRows(totalDataRef.current);
        }

        const title = totalTitle(section, sideSummary);
        const useNormalLayout = isEnabled(compact) || isEnabled(sideSummary) || section.id === "genka";
        const fixedHeight = section.id === "genka"
            ? genkaSummaryFixedHeight(totalPanel)
            : (isEnabled(compact) ? "96px" : "300px");

        const totalGrid = (
            <EntryGridAr
                id={fnc.current.GridID(gridPanel) + "_total_" + resolvedTotalType}
                forceNormalLayout={useNormalLayout}
                fixedNormalHeight={fixedHeight}
                hideRowNumbers={isEnabled(sideSummary)}
                rowDrag={false}
                entrydataRef={entrydataRef}
                EntrybodyRef={EntrybodyRef}
                dataRef={totalDataRef}
                columnRef={ensureTotalColRef(totalPanel)}
                columninfo={columninfo}
                columns={columns}
                QuerySetColumn={(colDef) => buildTotalGridColumns(section, colDef)}
                fnc={fnc}
                onFirstDataRendered={(params) => {
                    onFirstTotalDataRendered(params, totalPanel);
                }}
                formid={formid}
                ValueChanged={(params) => storeTotalRowData(totalPanel, DicVal(params, "api", null), params)}
                panel={totalPanel}
                total={true}
            />
        );

        return renderTotalGridContainer(section, sideSummary, title, totalGrid);
    }

    // サンプル情報セクションを複合カードとして描画する。
    function renderSampleInfoSection(section) {
        const baseSection = { ...section, component: "base", panelType: PANEL_TYPE.SAMPLE_INFO };
        const commentSection = { ...section, component: "nb", panelType: PANEL_TYPE.SAMPLE_COMMENT };
        const detailSection = { ...section, component: "grid", panelType: PANEL_TYPE.SAMPLE_DETAIL, totalType: PANEL_TYPE.SAMPLE_TOTAL };

        const basePanel = resolveSectionPanel(baseSection);
        const commentPanel = resolveRemarksSectionPanel(commentSection);
        const detailPanel = resolveGridSectionPanel(detailSection);
        const sampleTotalType = sectionTotalType(section, detailPanel);

        return renderSampleSectionBody({
            section,
            basePanel,
            commentPanel,
            detailPanel,
            sampleTotalType,
            renderContents: Contents,
            renderTotalGrid,
            sectionGridRowStyle,
            isEmpty
        });
    }

    // セクション本体を描画する。
    function renderSectionBody(section) {
        const renderModel = sectionRenderModelResolver.buildSectionRenderModel(section);
        return renderSectionBodyContent({
            section,
            renderModel,
            sectionGridRowStyle,
            resolveGridIndex,
            EmptyPanel,
            Contents,
            renderActionArea,
            renderSustainableImagePanel,
            renderTotalGrid,
            renderSampleInfoSection,
            renderGenkaHeader,
            renderDemandHeader,
            buildDemandColumns,
            buildNouhinHeaderColumns,
            buildGenkaFixedColumns,
            buildBrandNameGridColumns,
            buildDemeritGridColumns,
            colorwayDetailParams,
            toStableAutoHeightPanel,
            genkaGridFixedHeight,
            isDemandSectionId,
            sectionTotalType,
            isEmpty
        });
    }

    // セクションカード全体を描画する。
    function renderSection(section) {
        const isCollapsed = collapsedSections[section.id] === true;
        return (
            <SectionCard
                section={section}
                isCollapsed={isCollapsed}
                sectionCloseEnabled={sectionclose}
                setSectionRef={(elem) => { sectionRefs.current[section.id] = elem; }}
                onToggleCollapsed={() =>
                    setCollapsedSections((prev) => ({
                        ...prev,
                        [section.id]: !isCollapsed
                    }))
                }
                headerAction={renderSectionHeaderAction(section)}
            >
                {renderSectionBody(section)}
            </SectionCard>
        );
    }

    return {
        renderTotalGrid,
        renderSectionBody,
        renderSection
    };
}
